import type { WorkItem } from '../types/models';

/**
 * Today is the server's saved plan for the person, on every surface (the
 * Dashboard, the Work Items Today tab, the Mac, iPhone and Windows). Nothing in
 * the browser chooses today's work: this resolves the plan against the latest
 * items and says when what's on screen is old. Mirrors CadenceKit's TodayPlan.
 */

/** A plan on screen older than this offers a refresh ("Not updated since 10:42"). */
export const PLAN_STALE_AFTER_MS = 30 * 60 * 1000;

/** How often an open, visible Dashboard re-reads the plan by itself. */
export const PLAN_REFRESH_EVERY_MS = 5 * 60 * 1000;

/** Open as the plan counts it: not finished, cancelled or parked (Deferred). */
const isOpen = (item: WorkItem) => item.status !== 'Done' && item.status !== 'Cancelled' && item.status !== 'Deferred';

/**
 * Today's list: the plan's open items in the plan's order, taken from `items`
 * (so a status changed since the plan was read shows, and a finished item leaves
 * for Completed). Until the plan has loaded (`plannedIds` null) there is no
 * list: the browser never picks today's work itself (a raw "dated today" list
 * is how 1 Oct showed 46 items), so the page says it's loading or failed.
 */
export function todayFromPlan(items: WorkItem[], plannedIds: string[] | null): WorkItem[] {
  if (!plannedIds) return [];
  const byId = new Map(items.map(i => [i.id, i] as const));
  return plannedIds.map(id => byId.get(id)).filter((i): i is WorkItem => !!i && isOpen(i));
}

/** Whether the plan on screen is old enough to offer a refresh; never before the first read. */
export function isPlanStale(fetchedAt: number | null, now: number): boolean {
  return fetchedAt !== null && now - fetchedAt > PLAN_STALE_AFTER_MS;
}
